import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';

import AnimationCircle from './AnimationCircle';
import {
  selectMonthlyStatsData,
  selectMonthlyStatsLoading,
} from '../slices/reportsSlice';

const SVG_W = 260;
const SVG_H = 64;
const PAD_X = 18;
const PAD_Y = 10;

const MARK_STYLES = {
  best: {
    dot: 'fill-green-500 dark:fill-green-400',
    label: 'text-green-600 dark:text-green-400',
  },
  worst: {
    dot: 'fill-amber-500 dark:fill-amber-400',
    label: 'text-amber-600 dark:text-amber-400',
  },
  current: {
    dot: 'fill-brand-500 dark:fill-brand-400',
    label: 'text-brand-600 dark:text-brand-300',
  },
};

const Sparkline = ({points}) => {
  if (points.length < 2) return null;

  const ranks = points.map((p) => p.rank);
  const minRank = Math.max(1, Math.min(...ranks) - 1);
  const maxRank = Math.max(...ranks) + 1;
  const rankSpan = Math.max(1, maxRank - minRank);
  const xStep = points.length > 1 ? (SVG_W - PAD_X * 2) / (points.length - 1) : 0;

  // Lower rank (better) should be higher on chart → invert Y.
  const yFor = (rank) => PAD_Y + ((rank - minRank) / rankSpan) * (SVG_H - PAD_Y * 2);
  const xFor = (i) => PAD_X + i * xStep;

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(2)} ${yFor(p.rank).toFixed(2)}`)
    .join(' ');

  // Build current / best / worst markers, deduped by index. Priority when
  // indices coincide: current > best > worst (current is primary context).
  const lastIdx = points.length - 1;
  let bestIdx = 0;
  let worstIdx = 0;
  points.forEach((p, i) => {
    if (p.rank < points[bestIdx].rank) bestIdx = i;
    if (p.rank > points[worstIdx].rank) worstIdx = i;
  });
  const marks = [];
  const seen = new Set();
  const addMark = (idx, kind) => {
    if (seen.has(idx)) return;
    seen.add(idx);
    marks.push({ idx, kind, rank: points[idx].rank });
  };
  addMark(lastIdx, 'current');
  addMark(bestIdx, 'best');
  addMark(worstIdx, 'worst');

  return (
    <div className="relative w-full h-full">
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        className="w-full h-full overflow-visible"
        preserveAspectRatio="none"
      >
        <path
          d={pathD}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-brand-500 dark:text-brand-400"
        />
        {marks.map((m) => (
          <circle
            key={m.kind}
            cx={xFor(m.idx)}
            cy={yFor(m.rank)}
            r={m.kind === 'current' ? 3.5 : 3}
            className={MARK_STYLES[m.kind].dot}
          />
        ))}
      </svg>
      {marks.map((m) => {
        const leftPct = (xFor(m.idx) / SVG_W) * 100;
        const topPct = (yFor(m.rank) / SVG_H) * 100;
        // Clamp horizontal anchor so edge labels don't clip.
        const anchor =
          leftPct < 12 ? 'translate(0, -140%)'
            : leftPct > 88 ? 'translate(-100%, -140%)'
              : 'translate(-50%, -140%)';
        return (
          <div
            key={`lbl-${m.kind}`}
            className={`absolute pointer-events-none text-[10px] font-semibold font-mono tabular-nums whitespace-nowrap ${MARK_STYLES[m.kind].label}`}
            style={{ left: `${leftPct}%`, top: `${topPct}%`, transform: anchor }}
          >
            #{m.rank}
          </div>
        );
      })}
    </div>
  );
};

export default function MonthlyDailyRanks({activeUserId}) {
  const monthlyStatsData = useSelector(selectMonthlyStatsData);
  const loading = useSelector(selectMonthlyStatsLoading);

  const isForLoggedInUser =
    activeUserId && monthlyStatsData && monthlyStatsData.user_id === activeUserId;

  const points = useMemo(() => {
    if (!isForLoggedInUser) return [];
    const raw = monthlyStatsData?.daily_ranks_json;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((d) => d && d.rank != null && d.target_day)
      .sort((a, b) => String(a.target_day).localeCompare(String(b.target_day)));
  }, [monthlyStatsData, isForLoggedInUser]);

  if (!activeUserId) return null;

  if (loading && points.length === 0) {
    return (
      <div className="flex items-center justify-center py-6">
        <AnimationCircle />
      </div>
    );
  }

  if (!isForLoggedInUser || points.length === 0) {
    return (
      <div className="py-6 text-center text-xs text-gray-400 dark:text-gray-500">
        No data for the selected period.
      </div>
    );
  }

  const last = points[points.length - 1];
  const ranks = points.map((p) => p.rank);
  const bestRank = Math.min(...ranks);
  const worstRank = Math.max(...ranks);
  // Avg of daily ranks across the user's submitted days. Computed client-side
  // from the same points stream the sparkline uses, so this matches the
  // Analytics "AVG DAILY RANK" strip (which derives from selfPeerRow.avg_rank
  // — a float). monthlyStatsData.avg_rank is integer-truncated and would not
  // match Analytics' two-decimal value.
  const avgRank = points.reduce((sum, p) => sum + p.rank, 0) / points.length;
  // Show qualified forecasters only (those that appear in month_scores_json),
  // since the leaderboard ranks against this set. The engine's
  // total_forecasters field counts everyone including unqualified users.
  const qualifiedScores = monthlyStatsData?.month_scores_json;
  const totalForecasters = Array.isArray(qualifiedScores)
    ? qualifiedScores.length
    : last.total_forecasters;

  return (
    <div className="w-full h-full flex items-stretch gap-4">
      <div className="flex flex-col justify-center shrink-0">
        <p className="tabular-nums">
          <span className="font-mono text-4xl font-light tracking-tight text-gray-900 dark:text-gray-100">
            #{avgRank.toFixed(2)}
          </span>
          {totalForecasters != null && (
            <span className="ml-1 text-sm text-gray-400 dark:text-gray-500">
              of {totalForecasters}
            </span>
          )}
        </p>
        <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
          avg daily rank · latest{' '}
          <span className="font-mono font-semibold text-brand-600 dark:text-brand-300">
            #{last.rank}
          </span>
        </p>
        <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500 tabular-nums flex items-center gap-1 flex-wrap">
          <span>
            best <span className="font-mono text-green-600 dark:text-green-400 font-semibold">#{bestRank}</span>
            {' · '}
            worst <span className="font-mono text-amber-600 dark:text-amber-400 font-semibold">#{worstRank}</span>
            {' · '}
            {points.length} day{points.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      <div className="flex-1 min-w-0 min-h-[80px]">
        <Sparkline points={points} />
      </div>
    </div>
  );
}
